import React, { useMemo, useState } from 'react';

import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { getModificationQueryOptions } from 'src/services/modification/modificationQueries';
import { TableRowTypes } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/constants';
import type { TaskFormTableRow } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/types';

import { applyModificationMatrix, getMatrixTasks, toRegionTaskModifications } from '../taskFormUtils';

import ModificationList from './modificationList';
import ModificationMatrix from './modificationMatrix';

// **** Types **** //

interface ModificationEditorProps {
  row: TaskFormTableRow | null;
  onClose: () => void;
  onSubmit: (updatedRow: TaskFormTableRow) => void;
}

// **** Components **** //

/**
 * Container for editing the modifications assigned to a task or task-group. Owns
 * the selection state, the modification catalog fetch, and the header/footer
 * chrome, and renders a presentational `ModificationList` (single task) or
 * `ModificationMatrix` (task-group) in between.
 *
 * The catalog is every modification in the system; a checked box means the
 * modification is assigned. Meant to be mounted only while a row is selected
 * (see shared `Sheet`), so its state is seeded fresh from the row on each open
 * and torn down on close.
 */
const ModificationEditor = ({ row, onClose, onSubmit }: ModificationEditorProps) => {
  const modificationsQuery = useQuery(getModificationQueryOptions.modifications());
  const modifications = useMemo(
    () => toRegionTaskModifications(modificationsQuery.data ?? []),
    [modificationsQuery.data],
  );

  const tasks = row ? getMatrixTasks(row) : [];

  // Maps a taskId to the set of modificationIds assigned to it. Seeded from the
  // row's current assignments (available synchronously, independent of the catalog).
  const [enabledByTask, setEnabledByTask] = useState<Record<number, Set<number>>>(() => {
    const seed: Record<number, Set<number>> = {};
    tasks.forEach((task) => {
      seed[task.taskId] = new Set(task.modifications.map((m) => m.modificationId));
    });
    return seed;
  });

  // Meridian keeps the sheet's children mounted through the close animation,
  // during which the selected row is already null. Nothing to render then.
  if (!row) return null;

  const toggleCell = (taskId: number, modificationId: number, checked: boolean) => {
    setEnabledByTask((prev) => {
      const next = new Set(prev[taskId] ?? []);
      if (checked) {
        next.add(modificationId);
      } else {
        next.delete(modificationId);
      }
      return { ...prev, [taskId]: next };
    });
  };

  // Single-task list toggles the one and only task.
  const toggleListItem = (modificationId: number, checked: boolean) =>
    toggleCell(tasks[0].taskId, modificationId, checked);

  // Tri-state for the leading "select all" checkbox: true when every task has
  // the modification, false when none do, "mixed" when some do.
  const rowCheckState = (modificationId: number): boolean | 'mixed' => {
    const enabledCount = tasks.filter((task) => enabledByTask[task.taskId]?.has(modificationId)).length;
    if (enabledCount === 0) return false;
    if (enabledCount === tasks.length) return true;
    return 'mixed';
  };

  const toggleRow = (modificationId: number, checked: boolean) => {
    setEnabledByTask((prev) => {
      const next: Record<number, Set<number>> = {};
      tasks.forEach((task) => {
        const set = new Set(prev[task.taskId] ?? []);
        if (checked) {
          set.add(modificationId);
        } else {
          set.delete(modificationId);
        }
        next[task.taskId] = set;
      });
      return next;
    });
  };

  const onApply = () => onSubmit(applyModificationMatrix(row, enabledByTask, modifications));

  return (
    <Column spacing="400" height="100%" maxWidth="90vw" heights={['fit', 'fit', 'fill', 'fit', 'fit']}>
      {/* Header */}
      <Row alignmentHorizontal="justify">
        <Text type="h300">{`${row?.label} Modifications`}</Text>
        <Button type="icon" onClick={onClose} size="medium">
          <Icon tokens={closeTokens}>Close sheet</Icon>
        </Button>
      </Row>
      <Divider />

      {/* Body */}
      <Column overflowY="auto" height="100%" minHeight={0}>
        <LoadingSpinnerWrapper showSpinner={modificationsQuery.isPending}>
          {row?.type === TableRowTypes.TaskGroup ? (
            <ModificationMatrix
              modifications={modifications}
              tasks={tasks}
              enabledByTask={enabledByTask}
              rowCheckState={rowCheckState}
              onToggleCell={toggleCell}
              onToggleRow={toggleRow}
            />
          ) : (
            <ModificationList
              modifications={modifications}
              enabled={enabledByTask[tasks[0].taskId] ?? new Set<number>()}
              onToggle={toggleListItem}
            />
          )}
        </LoadingSpinnerWrapper>
      </Column>

      <Divider />
      {/* Footer */}
      <Row alignmentHorizontal="end">
        <Button type="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" onClick={onApply} disabled={!modificationsQuery.isSuccess}>
          Apply
        </Button>
      </Row>
    </Column>
  );
};

// **** Export **** //

export default ModificationEditor;
